import React, { useState } from 'react';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentProtocolId?: number;
  currentSection?: string;
  currentQuestion?: string;
  apiBaseUrl: string;
  getAuthHeaders: () => Record<string, string>;
  isAdmin?: boolean;            // show the admin "View Feedback Dashboard" link
  onOpenDashboard?: () => void; // navigate to the dashboard (closes the modal)
}

export default function FeedbackModal({
  isOpen,
  onClose,
  currentProtocolId,
  currentSection,
  currentQuestion,
  apiBaseUrl,
  getAuthHeaders,
  isAdmin,
  onOpenDashboard
}: FeedbackModalProps) {
  const [formData, setFormData] = useState({
    type: 'bug' as 'bug' | 'feature' | 'other',
    severity: '' as '' | 'critical' | 'major' | 'minor',
    title: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Capture context
      const context = {
        url: window.location.href,
        protocolId: currentProtocolId,
        section: currentSection,
        question: currentQuestion,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      };

      const payload = {
        type: formData.type,
        severity: formData.severity || null,
        title: formData.title,
        description: formData.description,
        context: JSON.stringify(context)
      };

      const response = await fetch(`${apiBaseUrl}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to submit feedback');

      alert('Thank you for your feedback! We appreciate you helping us improve.');
      onClose();

      // Reset form
      setFormData({
        type: 'bug',
        severity: '',
        title: '',
        description: ''
      });
    } catch (error) {
      console.error('Feedback submission error:', error);
      alert('Failed to submit feedback. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content feedback-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>

        <h2>Send Feedback</h2>
        <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: isAdmin ? '1rem' : '1.5rem' }}>
          Help us improve by reporting bugs, suggesting features, or sharing your thoughts.
        </p>

        {isAdmin && onOpenDashboard && (
          <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #eee' }}>
            <button
              type="button"
              className="secondary-button"
              onClick={() => { onClose(); onOpenDashboard(); }}
            >
              View Feedback Dashboard (admin)
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="feedback-form">
          {/* Type */}
          <div className="form-group">
            <label>Type</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  value="bug"
                  checked={formData.type === 'bug'}
                  onChange={() => setFormData({...formData, type: 'bug', severity: ''})}
                />
                Bug Report
              </label>
              <label>
                <input
                  type="radio"
                  value="feature"
                  checked={formData.type === 'feature'}
                  onChange={() => setFormData({...formData, type: 'feature', severity: ''})}
                />
                Feature Request
              </label>
              <label>
                <input
                  type="radio"
                  value="other"
                  checked={formData.type === 'other'}
                  onChange={() => setFormData({...formData, type: 'other', severity: ''})}
                />
                Other
              </label>
            </div>
          </div>

          {/* Severity (only for bugs) */}
          {formData.type === 'bug' && (
            <div className="form-group">
              <label htmlFor="severity">Severity</label>
              <select
                id="severity"
                value={formData.severity}
                onChange={(e) => setFormData({...formData, severity: e.target.value as any})}
                className="form-control"
              >
                <option value="">Select severity...</option>
                <option value="critical">Critical - Blocks work completely</option>
                <option value="major">Major - Significant impact</option>
                <option value="minor">Minor - Low impact or cosmetic</option>
              </select>
            </div>
          )}

          {/* Title */}
          <div className="form-group">
            <label htmlFor="title">
              Title <span className="required">*</span>
            </label>
            <input
              id="title"
              type="text"
              required
              maxLength={100}
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Brief description"
              className="form-control"
            />
            <small>{formData.title.length}/100</small>
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description">
              Description <span className="required">*</span>
            </label>
            <textarea
              id="description"
              required
              maxLength={500}
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Detailed explanation. For bugs, please include steps to reproduce."
              className="form-control"
            />
            <small>{formData.description.length}/500</small>
          </div>

          {/* Submit */}
          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="secondary-button"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
